import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowRight as ArrowRightIcon,
  ClipboardText as ClipboardIcon,
  Plus as PlusIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { type Request } from '@/types/v2/request';
import { getRequestsByUnit } from '@/api/requests';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useDialog } from '@/hooks/v2/use-dialog';
import CreateRequestDialog from '@/pages/v2/requests/request/components/create-request-dialog';

interface UnitRequestsSectionProps {
  unitId: string;
  communityId: string;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  new:              { bg: '#3B82F618', text: '#3B82F6' },
  'initial review': { bg: '#F59E0B18', text: '#F59E0B' },
  quoting:          { bg: '#F59E0B18', text: '#F59E0B' },
  quoted:           { bg: '#3B82F618', text: '#3B82F6' },
  'final review':   { bg: '#F59E0B18', text: '#F59E0B' },
  approved:         { bg: '#10B98118', text: '#10B981' },
  rejected:         { bg: '#EF444418', text: '#EF4444' },
  scheduled:        { bg: '#6366F118', text: '#6366F1' },
  'quality review': { bg: '#F59E0B18', text: '#F59E0B' },
  completed:        { bg: '#10B98118', text: '#10B981' },
};

export function UnitRequestsSection({ unitId, communityId }: UnitRequestsSectionProps): React.JSX.Element {
  const navigate = useNavigate();
  const createDialog = useDialog();

  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['unit-requests', unitId, communityId],
    queryFn: () => getRequestsByUnit(communityId, unitId),
    enabled: Boolean(unitId) && Boolean(communityId),
  });

  return (
    <>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
              <ClipboardIcon size={14} />
            </Box>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
              Requests
            </Typography>
            {requests.length > 0 && (
              <Chip label={requests.length} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} variant="outlined" />
            )}
          </Stack>
          <Button onClick={createDialog.handleOpen} size="small" startIcon={<PlusIcon size={14} />} variant="outlined">
            New
          </Button>
        </Stack>

        {isLoading ? (
          <Stack spacing={1.5}>
            {[1, 2, 3].map((i) => (
              <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
                <Skeleton height={36} variant="rectangular" width={36} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton height={14} variant="text" width="60%" />
                  <Skeleton height={12} variant="text" width="30%" />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : requests.length === 0 ? (
          <Typography color="text.disabled" sx={{ fontStyle: 'italic', py: 2 }} variant="body2">
            No requests for this unit.
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {requests.slice(0, 10).map((request: Request) => {
              const statusName = request.request_status?.name?.toLowerCase() ?? '';
              const statusStyle = STATUS_COLORS[statusName] ?? { bg: '#6B728018', text: '#6B7280' };
              const dt = dayjs(request.created_at);

              return (
                <Stack
                  key={request.id}
                  direction="row"
                  spacing={1.5}
                  onClick={() => navigate(`/requests/${request.uuid ?? request.id}`)}
                  sx={{
                    alignItems: 'center',
                    py: 1.5,
                    px: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.12s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'background.default',
                      color: 'text.secondary',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      flexDirection: 'column',
                      lineHeight: 1,
                    }}
                  >
                    <Box component="span" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {dt.format('MMM')}
                    </Box>
                    <Box component="span" sx={{ fontSize: '0.85rem', fontWeight: 800 }}>
                      {dt.format('DD')}
                    </Box>
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                      {request.summary}
                    </Typography>
                    <Typography color="text.disabled" sx={{ fontSize: '0.7rem', mt: 0.25 }}>
                      {request.request_type?.name ? toTitleCase(request.request_type.name) : ''}
                      {request.display_id ? ` · ${request.display_id}` : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={toTitleCase(request.request_status?.name ?? '')}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      bgcolor: statusStyle.bg,
                      color: statusStyle.text,
                      border: `1px solid ${statusStyle.text}40`,
                    }}
                  />
                </Stack>
              );
            })}
            {requests.length > 10 && (
              <Typography color="text.disabled" sx={{ py: 1, textAlign: 'center', fontSize: '0.75rem' }}>
                + {requests.length - 10} more requests
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      <CreateRequestDialog
        communityId={communityId}
        onClose={() => { createDialog.handleClose(); refetch(); }}
        open={createDialog.open}
        unitId={unitId}
      />
    </>
  );
}
